export { sf311CaseSourceUrl } from './source-links';

import { addendaDetails, isAddendaTable } from './addenda';
import {
  enforcementDetails,
  novResolved,
  relatedEnforcementRecords,
  relatedEnforcementSources,
} from './enforcement';
import { isExplicitlyClosed } from './issues/status';
import type {
  ParcelRecord,
  ParcelSnapshot,
  PermitActivity,
  RecordSummary,
  PropertySummary,
  SourceCoverage,
} from './types';

export function isClosed(status: string) {
  return isExplicitlyClosed(status);
}

function dateValue(value: string) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

const PERMIT_KINDS = new Set(['building', 'electrical', 'plumbing']);
const ACTIVITY_DATE_FIELDS = new Set([
  'datefiled',
  'fileddate',
  'filingdate',
  'applicationdate',
  'dateapplied',
  'submitteddate',
  'datesubmitted',
  'receiveddate',
  'datereceived',
  'issueddate',
  'dateissued',
  'statusdate',
  'currentstatusdate',
  'stagedate',
  'datecompleted',
  'completeddate',
  'completiondate',
  'inspectiondate',
  'lastinspectiondate',
  'activitydate',
]);
const ADDENDA_DATE_FIELDS = new Set(['arrive', 'start', 'inhold', 'outhold', 'finish', 'received']);
const normalizedLabel = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
const NON_ACTIVITY_CONTEXT = /schedul|appointment|expir|assessment|valuation|birth/i;
const ACTIVITY_STATE_FIELDS = new Set([
  'status',
  'currentstatus',
  'inspectionstatus',
  'result',
  'inspectionresult',
  'stage',
  'type',
  'inspectiontype',
  'activity',
  'reviewresult',
]);

// City dates identify calendar days. Reject impossible dates instead of allowing
// Date.parse to roll February 31 into March or interpret a permit ID as a year.
function activityDate(value: string) {
  const input = value.trim();
  const iso = input.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2}))?$/,
  );
  const us = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  const named = input.match(/^(\d{1,2})[- ]([A-Za-z]{3})[- ](\d{2}|\d{4})$/);
  const months = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
  ];
  if (!iso && !us && !named) return null;
  const rawYear = iso?.[1] ?? us?.[3] ?? named?.[3] ?? '';
  let year = Number(rawYear);
  if (rawYear.length === 2) year += year < 70 ? 2000 : 1900;
  const month = iso
    ? Number(iso[2])
    : us
      ? Number(us[1])
      : months.indexOf(named![2]!.toLowerCase()) + 1;
  const day = Number(iso?.[3] ?? us?.[2] ?? named?.[1]);
  const date = new Date(Date.UTC(year, month - 1, day));
  const hour = iso ? input.match(/T(\d{2}):/)?.[1] : undefined;
  if (
    year < 1800 ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day ||
    (hour !== undefined && Number(hour) > 23) ||
    (iso && input.includes('T') && !Number.isFinite(Date.parse(input)))
  )
    return null;
  return date.getTime();
}

export function activityReferenceDay(value: string) {
  const input = value.trim();
  const calendarDay = activityDate(input);
  if (calendarDay === null || !input.includes('T')) return calendarDay;
  // Reference timestamps are instants, while the city's recorded dates are
  // calendar days. Only the reference is converted into San Francisco time.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(input));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value;
  return activityDate(`${part('year')}-${part('month')}-${part('day')}`);
}

function twoYearsBefore(value: number) {
  const cutoff = new Date(value);
  const month = cutoff.getUTCMonth();
  cutoff.setUTCFullYear(cutoff.getUTCFullYear() - 2);
  if (cutoff.getUTCMonth() !== month) cutoff.setUTCDate(0);
  return cutoff.getTime();
}

function permitActivity(
  record: Pick<RecordSummary, 'date' | 'pages'>,
  asOf: number | null,
): PermitActivity {
  const candidates = [record.date];
  for (const page of record.pages ?? []) {
    if (NON_ACTIVITY_CONTEXT.test(page.title)) continue;
    const fields = Object.entries(page.fields);
    const nonActivityFields = fields.some(
      ([label, value]) =>
        ACTIVITY_STATE_FIELDS.has(normalizedLabel(label)) && NON_ACTIVITY_CONTEXT.test(value),
    );
    if (!nonActivityFields) {
      for (const [label, value] of fields) {
        if (ACTIVITY_DATE_FIELDS.has(normalizedLabel(label))) candidates.push(value);
      }
    }
    for (const table of page.tables) {
      if (NON_ACTIVITY_CONTEXT.test(table.title)) continue;
      const labels = table.headers.map(normalizedLabel);
      const addenda = isAddendaTable(table);
      const activityTable =
        /status|stages?|inspection|addenda/i.test(table.title) ||
        labels.some((label) =>
          ['stage', 'status', 'inspection', 'inspectiontype', 'inspectionresult'].includes(label),
        );
      for (const row of table.rows) {
        if (
          labels.some(
            (label, index) =>
              ACTIVITY_STATE_FIELDS.has(label) && NON_ACTIVITY_CONTEXT.test(row[index] ?? ''),
          )
        )
          continue;
        table.headers.forEach((_, index) => {
          const label = labels[index] ?? '';
          if (
            ACTIVITY_DATE_FIELDS.has(label) ||
            (addenda && ADDENDA_DATE_FIELDS.has(label)) ||
            (activityTable && label === 'date')
          ) {
            candidates.push(row[index] ?? '');
          }
          // DBI review notes begin with the event date. Dates mentioned later in
          // prose can describe expiry, appointments or older work, so ignore them.
          if (addenda && label === 'holddescription') {
            const note = (row[index] ?? '').match(
              /^(\d{1,2}\/\d{1,2}\/(?:\d{4}|\d{2}))\s*:\s*(\S.*)$/,
            );
            if (
              note &&
              !/^(?:expir|birth|assessment|valuation|schedul|appointment|permit\s+expir)/i.test(
                note[2]!,
              )
            ) {
              candidates.push(note[1]!);
            }
          }
        });
      }
    }
  }
  const dates = candidates
    .map(activityDate)
    .filter((date): date is number => date !== null && asOf !== null && date <= asOf);
  if (!dates.length || asOf === null)
    return {
      classification: 'unknown',
      lastActivityAt: null,
      reason: 'activity-date-unavailable',
    };
  const latest = dates.reduce((latest, date) => Math.max(latest, date), 0);
  const recent = latest >= twoYearsBefore(asOf);
  return {
    classification: recent ? 'recent' : 'historical',
    lastActivityAt: new Date(latest).toISOString().slice(0, 10),
    reason: recent ? 'recent-activity' : 'no-recent-activity',
  };
}

export function classifyPermitActivity(
  record: Pick<RecordSummary, 'kind' | 'date' | 'pages'>,
  asOf: string,
) {
  return PERMIT_KINDS.has(record.kind)
    ? permitActivity(record, activityReferenceDay(asOf))
    : undefined;
}

function linkedAddressFallback(addresses: string[]) {
  if (!addresses.length) return '';
  const parsed = addresses.map((address) => {
    const [number = '', ...streetParts] = address.split(/\s+/);
    return {
      address,
      number,
      street: streetParts.join(' '),
      words: streetParts.length,
    };
  });
  const minimumWords = Math.min(...parsed.map((address) => address.words));
  const baseAddresses = parsed.filter((address) => address.words === minimumWords);
  const streetGroups = new Map<string, typeof baseAddresses>();
  for (const address of baseAddresses) {
    streetGroups.set(address.street, [...(streetGroups.get(address.street) ?? []), address]);
  }
  const largestGroup = [...streetGroups.values()].sort((a, b) => b.length - a.length)[0] ?? [];
  if (!largestGroup.length) return addresses[0];
  const numbers = [...new Set(largestGroup.map((address) => address.number))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
  return numbers.length > 1
    ? `${numbers[0]}-${numbers.at(-1)} ${largestGroup[0]!.street}`
    : largestGroup[0]!.address;
}

const ADDRESS_SUFFIXES: Record<string, string> = {
  AV: 'Ave',
  AVE: 'Ave',
  AVENUE: 'Ave',
  ST: 'St',
  STREET: 'St',
  BLVD: 'Blvd',
  BOULEVARD: 'Blvd',
  RD: 'Rd',
  ROAD: 'Rd',
  DR: 'Dr',
  DRIVE: 'Dr',
  CT: 'Ct',
  COURT: 'Ct',
  LN: 'Ln',
  LANE: 'Ln',
  PL: 'Pl',
  PLACE: 'Pl',
  TER: 'Ter',
  TERRACE: 'Ter',
  CIR: 'Cir',
  CIRCLE: 'Cir',
  HWY: 'Hwy',
  HIGHWAY: 'Hwy',
  ALY: 'Aly',
  ALLEY: 'Aly',
};
const COMMON_ADDRESS_SUFFIXES = [
  'AV',
  'AVE',
  'AVENUE',
  'ST',
  'STREET',
  'BLVD',
  'BOULEVARD',
  'RD',
  'ROAD',
  'DR',
  'DRIVE',
  'TER',
  'TERRACE',
  'CIR',
  'CIRCLE',
  'HWY',
  'HIGHWAY',
];
const CONTEXT_ADDRESS_SUFFIXES = ['CT', 'COURT', 'LN', 'LANE', 'PL', 'PLACE', 'ALY', 'ALLEY'];
const ADDRESS_SUFFIX_PATTERN = Object.keys(ADDRESS_SUFFIXES).join('|');
const ADDRESS_UNIT_PATTERN = '(?:unit|apt|apartment|suite|ste|fl|floor|bldg|building)';
const ADDRESS_DIRECTION_PATTERN = '(?:n|s|e|w|ne|nw|se|sw|north|south|east|west)';
const COMMON_SUFFIX_EXPRESSION = new RegExp(
  '\\b(' + COMMON_ADDRESS_SUFFIXES.join('|') + ')\\.?(?=\\s|,|\\/|&|$)',
  'gi',
);
const CONTEXT_SUFFIX_EXPRESSION = new RegExp(
  '\\b(' +
    CONTEXT_ADDRESS_SUFFIXES.join('|') +
    ')\\.?(?=(?:\\s+(?:#|' +
    ADDRESS_UNIT_PATTERN +
    '\\b|' +
    ADDRESS_DIRECTION_PATTERN +
    '\\b|&|and\\b).*?)?\\s*(?:,|\\/|&|$))',
  'gi',
);
const ADDRESS_WORD_EXPRESSION = /\b[A-Za-z]+(?:'[A-Za-z]+)*\b/g;
const ADDRESS_WORD_EXCEPTIONS = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'AS',
  'GU',
  'MP',
  'PR',
  'VI',
  'SF',
  'PO',
  'N',
  'S',
  'E',
  'W',
]);
const RANGE_ZERO_EXPRESSION = new RegExp(
  "\\b(\\d+[A-Z]?[-–]\\s*)0+(\\d+[A-Z]?)(?=(?:[-–]\\s*\\d+[A-Z]?)*\\s+[A-Z0-9'&./ -]*?\\s+(?:" +
    ADDRESS_SUFFIX_PATTERN +
    ')\\b)',
  'gi',
);
const UNIT_VALUE_EXPRESSION = new RegExp(
  '(?:\\s+#\\s*|\\s+\\b' + ADDRESS_UNIT_PATTERN + '\\.?\\s+)([a-z0-9-]+)',
  'gi',
);
const LEADING_UNIT_EXPRESSION = new RegExp(
  '^(?:#\\s*|' + ADDRESS_UNIT_PATTERN + '\\.?\\s+)([a-z0-9-]+)',
  'i',
);
const UNIT_MARKER_EXPRESSION = new RegExp(
  '\\s+(?:#\\s*|\\b' + ADDRESS_UNIT_PATTERN + '\\.?)\\s*(?=(?:,|$))',
  'gi',
);
const PROPERTY_ADDRESS_EXPRESSION = new RegExp(
  "\\b\\d+[A-Z]?(?:\\s*[-–]\\s*\\d+[A-Z]?)*\\s+[A-Z0-9][A-Z0-9'&./ -]{0,60}?\\s+(?:" +
    ADDRESS_SUFFIX_PATTERN +
    ')\\.?(?=\\s|,|$)(?:\\s+(?:#\\s*|' +
    ADDRESS_UNIT_PATTERN +
    '\\.?\\s+)[A-Z0-9-]+)?',
  'gi',
);

function addressSuffix(suffix: string) {
  return ADDRESS_SUFFIXES[suffix.replace('.', '').toUpperCase()] ?? suffix;
}
function addressWord(word: string) {
  const upper = word.toUpperCase();
  if (word === upper && ADDRESS_WORD_EXCEPTIONS.has(upper)) return word;
  if (word !== upper && word !== word.toLowerCase()) return word;
  return word
    .toLowerCase()
    .replace(
      /(^|')([a-z])/g,
      (_, boundary: string, letter: string) => boundary + letter.toUpperCase(),
    );
}
function addressUnit(unit: string) {
  return (
    'Unit ' +
    unit
      .replace(/^0+(?=\d)/, '')
      .replace(/([-–])0+(?=\d)/g, '$1')
      .toUpperCase()
  );
}
function stripAddressNumberZeros(value: string) {
  let result = value;
  let previous: string;
  do {
    previous = result;
    result = result
      .replace(/(^|[/&,]\s*)0+(\d+[A-Z]?)/g, '$1$2')
      .replace(RANGE_ZERO_EXPRESSION, '$1$2');
  } while (result !== previous);
  return result;
}

/** Human-facing labels only; preserve original city fields in source records. */
export function formatPropertyAddress(address: string) {
  let label = address.trim().replace(/\s+/g, ' ');
  label = stripAddressNumberZeros(label);
  return label
    .replace(/\b0+(\d+(?:st|nd|rd|th))\b/gi, '$1')
    .replace(COMMON_SUFFIX_EXPRESSION, addressSuffix)
    .replace(CONTEXT_SUFFIX_EXPRESSION, addressSuffix)
    .replace(ADDRESS_WORD_EXPRESSION, (word, offset: number, value: string) => {
      const before = value.slice(0, offset);
      const openTag = before.lastIndexOf('<');
      if (openTag > before.lastIndexOf('>')) return word;
      const tokenStart =
        Math.max(before.lastIndexOf(' '), before.lastIndexOf(','), before.lastIndexOf('/')) + 1;
      const rest = value.slice(offset);
      const tokenEnd = offset + (rest.search(/[\s,/]/) < 0 ? rest.length : rest.search(/[\s,/]/));
      if (value.slice(tokenStart, tokenEnd).includes('@')) return word;
      return addressWord(word);
    })
    .replace(
      /(\d)(ST|ND|RD|TH)\b/gi,
      (_, digit: string, ordinal: string) => digit + ordinal.toLowerCase(),
    )
    .replace(LEADING_UNIT_EXPRESSION, (_, unit: string) => addressUnit(unit))
    .replace(UNIT_VALUE_EXPRESSION, (_, unit: string) => ' ' + addressUnit(unit))
    .replace(/\bUnit\s+([A-Z0-9-]+)\s+Unit\s+([A-Z0-9-]+)\b/g, 'Unit $1-$2')
    .replace(/,\s+(?=Unit\s)/g, ' ')
    .replace(UNIT_MARKER_EXPRESSION, '')
    .replace(
      /,\s*([a-z]{2})(?=\s+\d{5}\b|$)/gi,
      (_, region: string) => ', ' + region.toUpperCase(),
    );
}

const ADDRESS_KEYWORD_EXPRESSION = new RegExp(
  '^(?:' +
    ADDRESS_SUFFIX_PATTERN +
    '|' +
    ADDRESS_UNIT_PATTERN +
    '|' +
    ADDRESS_DIRECTION_PATTERN +
    ')\\.?$',
  'i',
);

/** Normalize address-shaped labels inside user-facing prose without touching evidence. */
export function formatPropertyAddressText(text: string): string {
  return text.replace(PROPERTY_ADDRESS_EXPRESSION, (address) => {
    // A count in mixed-case prose ("2 permits changed at 99 Example St") is not a house
    // number: keep its words and format only the address that follows.
    const prose =
      /[A-Z]/.test(address) &&
      address
        .split(/\s+/)
        .slice(1)
        .some((word) => /^[a-z]/.test(word) && !ADDRESS_KEYWORD_EXPRESSION.test(word));
    if (prose) {
      const rest = address.search(/\s\d/);
      return rest < 0
        ? address
        : address.slice(0, rest) + formatPropertyAddressText(address.slice(rest));
    }
    const formatted = formatPropertyAddress(address);
    // The suffix's period may end the sentence; keep it.
    return address.endsWith('.') && !formatted.endsWith('.') ? `${formatted}.` : formatted;
  });
}

export function canonicalParcelAddress(snapshot: ParcelSnapshot) {
  return (
    snapshot.assessor?.fields['Assessor address'] ||
    linkedAddressFallback(snapshot.addresses) ||
    `Block ${snapshot.block}, Lot ${snapshot.lot}`
  );
}

export function summarizeSnapshot(
  snapshot: ParcelSnapshot,
  options: { asOf?: string } = {},
): PropertySummary {
  const asOf = activityReferenceDay(options.asOf ?? snapshot.fetchedAt);
  const activities = new Map(
    snapshot.records
      .filter((record) => PERMIT_KINDS.has(record.kind))
      .map((record) => {
        const detailStatus = snapshot.preview?.recordDetails[`${record.kind}:${record.id}`];
        return [
          record,
          detailStatus === 'pending' || detailStatus === 'failed'
            ? ({
                classification: 'unknown',
                lastActivityAt: null,
                reason: 'activity-date-unavailable',
              } satisfies PermitActivity)
            : permitActivity(record, asOf),
        ];
      }),
  );
  const recordDate = (record: ParcelRecord) => {
    const activity = activities.get(record);
    return activity ? dateValue(activity.lastActivityAt ?? '') : dateValue(record.date);
  };
  const sorted = [...snapshot.records].sort(
    (a, b) =>
      recordDate(b) - recordDate(a) ||
      (a.sourceOrder ?? Number.MAX_SAFE_INTEGER) - (b.sourceOrder ?? Number.MAX_SAFE_INTEGER),
  );
  const resolved = (record: ParcelRecord) =>
    isClosed(record.status) || novResolved(record, snapshot.records);
  const historicalPermits = sorted.filter(
    (record) =>
      !resolved(record) &&
      activities.has(record) &&
      (snapshot.preview
        ? activities.get(record)?.classification === 'historical'
        : activities.get(record)?.classification !== 'recent'),
  );
  const open = sorted.filter((record) => !resolved(record) && !historicalPermits.includes(record));
  const closed = sorted.filter(resolved);
  const twoYearsAgo = twoYearsBefore(asOf ?? 0);
  const olderClosed = closed.filter((record) => {
    const closedAt = recordDate(record);
    return closedAt > 0 && closedAt < twoYearsAgo;
  });
  const recentlyClosed = closed.filter((record) => !olderClosed.includes(record));
  const permits = sorted.filter((record) => PERMIT_KINDS.has(record.kind));
  const complaints = sorted.filter((record) => ['complaint', 'nov'].includes(record.kind));
  const openComplaints = open.filter((record) => record.kind === 'complaint');
  const closedComplaints = closed.filter((record) => record.kind === 'complaint');
  const openNovs = open.filter((record) => record.kind === 'nov');
  const closedNovs = closed.filter((record) => record.kind === 'nov');
  const summarizeRecord = (record: ParcelRecord) => {
    // A notice and its parent complaint remain independent monitored records.
    // The detail view includes the parent's complete case history with the NOV.
    const complaints =
      record.kind === 'nov' ? relatedEnforcementRecords(record, snapshot.records) : [];
    const enforcementUpdates = [record, ...complaints]
      .flatMap(enforcementDetails)
      .sort((a, b) => dateValue(b.date) - dateValue(a.date));
    return {
      id: record.id,
      kind: record.kind,
      status: record.status,
      date: record.date,
      address: record.address,
      description: record.description,
      sourceUrl: record.sourceUrl,
      ...(record.sourceOrder === undefined ? {} : { sourceOrder: record.sourceOrder }),
      detailStatus: snapshot.preview?.recordDetails[`${record.kind}:${record.id}`],
      detailObservedAt:
        snapshot.preview?.recordDetailMeta?.[`${record.kind}:${record.id}`]?.observedAt,
      detailAttemptedAt:
        snapshot.preview?.recordDetailMeta?.[`${record.kind}:${record.id}`]?.attemptedAt,
      detailError: snapshot.preview?.recordDetailMeta?.[`${record.kind}:${record.id}`]?.error,
      activity: activities.get(record),
      pages: [
        ...record.pages,
        ...complaints.flatMap((complaint) =>
          complaint.pages.map((page) => ({
            ...page,
            title: `Complaint ${complaint.id} · ${page.title}`,
          })),
        ),
      ],
      addenda: addendaDetails(record),
      enforcementUpdates,
      relatedSources: relatedEnforcementSources(record, snapshot.records),
    };
  };
  return {
    health: snapshot.health,
    runtime: snapshot.runtime,
    block: snapshot.block,
    lot: snapshot.lot,
    addresses: snapshot.addresses,
    displayAddress: canonicalParcelAddress(snapshot),
    fetchedAt: snapshot.fetchedAt,
    sourceUrl: snapshot.sourceUrl,
    preview: snapshot.preview,
    counts: {
      open: open.length,
      permits: permits.length,
      complaints: complaints.length,
      openComplaints: openComplaints.length,
      closedComplaints: closedComplaints.length,
      openNovs: openNovs.length,
      closedNovs: closedNovs.length,
      recordedDocuments: snapshot.recorder?.documents.length ?? 0,
      subdivisionProjects: snapshot.subdivision?.projects.length ?? 0,
      sf311Reports: snapshot.sf311?.cases.length ?? 0,
      openSf311Reports:
        snapshot.sf311?.cases.filter(
          (incident) => !isExplicitlyClosed(incident.status, incident.closedAt, 'sf311', '311'),
        ).length ?? 0,
      section604Affidavits: snapshot.safetyCompliance?.section604.affidavits.length ?? 0,
      fireInspections: snapshot.safetyCompliance?.fireInspections.inspections.length ?? 0,
      openFireViolations:
        snapshot.safetyCompliance?.fireViolations.violations.filter(
          (violation) =>
            !isExplicitlyClosed(
              violation.status,
              violation.closeDate,
              'fire-violations',
              'fire-violation',
            ),
        ).length ?? 0,
      fireComplianceStatements: snapshot.safetyCompliance?.statements.statements.length ?? 0,
    },
    open: open.map(summarizeRecord),
    historicalPermits: historicalPermits.map(summarizeRecord),
    recentlyClosed: recentlyClosed.map(summarizeRecord),
    olderClosed: olderClosed.map(summarizeRecord),
    latestActivity: sorted.slice(0, 7).map(summarizeRecord),
    assessor: snapshot.assessor ?? null,
    recorder: snapshot.recorder ?? null,
    subdivision: snapshot.subdivision ?? null,
    publicWorks: snapshot.publicWorks ?? null,
    sf311: snapshot.sf311 ?? null,
    safetyCompliance: snapshot.safetyCompliance ?? null,
  };
}

const SUPPLEMENTAL_PUBLIC_SOURCES = {
  recorder: 'recorder',
  subdivision: 'subdivision',
  'public-works': 'publicWorks',
} as const satisfies Partial<Record<SourceCoverage['source'], keyof ParcelSnapshot>>;

export type SupplementalPublicSource = keyof typeof SUPPLEMENTAL_PUBLIC_SOURCES;

/** Supplemental public sources a saved scan did not capture successfully. */
export function missingPublicSources(snapshot: ParcelSnapshot) {
  return (Object.keys(SUPPLEMENTAL_PUBLIC_SOURCES) as SupplementalPublicSource[]).filter(
    (source) =>
      snapshot.health.sources?.find((item) => item.source === source)?.status !== 'complete',
  );
}

/**
 * Add current public source data to a saved scan for display. The saved
 * snapshot itself remains the monitoring baseline.
 */
export function withPublicSource<S extends SupplementalPublicSource>(
  snapshot: ParcelSnapshot,
  coverage: SourceCoverage & { source: S },
  value: NonNullable<ParcelSnapshot[(typeof SUPPLEMENTAL_PUBLIC_SOURCES)[S]]>,
): ParcelSnapshot {
  return {
    ...snapshot,
    [SUPPLEMENTAL_PUBLIC_SOURCES[coverage.source]]: value,
    health: {
      ...snapshot.health,
      sources: [
        ...(snapshot.health.sources ?? []).filter((item) => item.source !== coverage.source),
        coverage,
      ],
    },
  };
}
